import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { styled } from '@mui/material';

import CustomerRequestConfirmationDialog from './ConfirmationDialog/CustomerRequestConfirmationDialog';
import { AppState } from 'helpers/store/models/AppState';
import { ClearContextEntityType, ClearContextRequestActionInputs } from 'models/context/ClearContext';
import { GetKahlPayload } from 'models/KahlMessage';
import {
  selectContextCustomerRequestDeeplinkUrl,
  selectContextCustomerRequestInfo,
} from 'saga/context/customerRequest/ducks';
import { clearContextAction } from 'saga/context/ducks';
import { selectContextInternalIsUserScreenFilterDisabled } from 'saga/context/internal/ducks';
import { getKahlActions } from 'saga/kahl/ducks';

declare type IFrameEventType = 'resizeCrPanel' | 'closeCrPanel' | 'showKahl';

interface IFrameEventData {
  eventType: IFrameEventType;
  eventData: any;
}

const IFrameWrapper = styled('iframe')(() => ({
  minWidth: '360px',
  maxWidth: 'calc(100% - 32px)',
  maxHeight: '90vh',
  position: 'fixed',
  bottom: '0',
  right: '16px',
  border: 'none',
  overflow: 'hidden',
  boxShadow: '0 0 30px 5px rgb(19 31 54 / 20%)',
  borderRadius: '8px',
  borderBottomLeftRadius: '0',
  borderBottomRightRadius: '0',
}));

const CustomerRequestContainer = ({
  crDetailDeeplink,
  closeCR,
  showKahl,
  isInternal,
}: StateToProps & DispatchToProps) => {
  const iframeRef = useRef<any>();

  const closeCustomerRequest = () => closeCR({ clearContextEntity: ClearContextEntityType.CUSTOMER_REQUEST });

  const [modalOpen, setModalOpen] = useState(false);
  const openCrConfirmationDialog = () => setModalOpen(true);
  const closeCrConfirmationDialog = () => setModalOpen(false);
  const confirmCrConfirmationDialog = () => {
    closeCrConfirmationDialog();
    closeCustomerRequest();
  };

  const messageEventHandler = (e: any) => {
    if (!e.isTrusted || !e.data) {
      return;
    }

    /**
     * Handles the message 'closeCrPanel' provided by iframe.
     */
    const doCloseCrPanel = (data?: { modalBeforeClosing: string }) => {
      if (data?.modalBeforeClosing === 'true') {
        openCrConfirmationDialog();
      } else {
        closeCustomerRequest();
      }
    };

    /**
     * Handles the message 'resizeCrPanel' provided by iframe.
     */
    const doResizeCrPanel = (data?: { width: string; height: string }) => {
      if (data?.width) {
        iframeRef.current.style.width = data.width;
      }

      if (data?.height) {
        iframeRef.current.style.height = data.height;
      }
    };

    /**
     * Handles the message 'showKahl' provided by iframe.
     */
    const doShowKahl = (data?: GetKahlPayload) => {
      if (data) {
        showKahl(data);
      }
    };

    const { eventType, eventData }: IFrameEventData = e.data;

    switch (eventType) {
      case 'closeCrPanel':
        doCloseCrPanel(eventData);

        break;
      case 'resizeCrPanel':
        doResizeCrPanel(eventData);

        break;
      case 'showKahl':
        doShowKahl(eventData);

        break;
    }
  };

  useEffect(() => {
    // componentDidMount
    window.addEventListener('message', messageEventHandler);

    return () => {
      // componentWillUnmount
      window.removeEventListener('message', messageEventHandler);
    };
  });

  return (
    <>
      {crDetailDeeplink && isInternal && (
        <>
          <IFrameWrapper
            ref={iframeRef}
            key={crDetailDeeplink}
            src={crDetailDeeplink}
            height={0}
            scrolling="no"
            sx={{ zIndex: '99999' }}
          />
          {modalOpen && (
            <CustomerRequestConfirmationDialog
              isOpen={modalOpen}
              onClose={closeCrConfirmationDialog}
              onConfirm={confirmCrConfirmationDialog}
            />
          )}
        </>
      )}
    </>
  );
};

interface StateToProps {
  crDetailDeeplink?: string;
  crRefNo?: string;
  isInternal: boolean;
}

interface DispatchToProps {
  closeCR: (payload: ClearContextRequestActionInputs) => void;
  showKahl: (payload?: GetKahlPayload) => void;
}

const mapStateToProps = (state: AppState): StateToProps => ({
  crDetailDeeplink: selectContextCustomerRequestDeeplinkUrl(state),
  crRefNo: selectContextCustomerRequestInfo(state)?.crRefNo,
  isInternal: selectContextInternalIsUserScreenFilterDisabled(state),
});

const mapDispatchToProps: DispatchToProps = {
  closeCR: clearContextAction.request,
  showKahl: getKahlActions.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerRequestContainer);
